import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';

export function FlutterCatch({ user, profile, onNavigate }) {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [scoreSaved, setScoreSaved] = useState(false);
  const [restartTrigger, setRestartTrigger] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef(null);
  const gameRef = useRef({});
  const [topScores, setTopScores] = useState([]);
  const [gameStats, setGameStats] = useState(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!gameOver) return;
    const g = gameRef.current;
    setGameStats({
      score:      g.score,
      hits:       g.hitsTotal,
      misses:     g.missesTotal,
      bestStreak: g.bestStreak,
    });
    setScoreSaved(false);
    supabase.from('fluttercatch_leaderboard').insert([{
      user_id:  user.id,
      score:    g.score,
      username: profile?.username || null,
      email:    user.email,
    }]).then(({ error }) => {
      if (error) console.error('Error saving score:', error);
      setScoreSaved(true);
      // Fetch after save so the user's new score is included
      supabase.from('fluttercatch_leaderboard')
        .select('*')
        .order('score', { ascending: false })
        .limit(10)
        .then(({ data }) => { if (data) setTopScores(data); });
    });
  }, [gameOver, user, profile]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const HUD_H    = 52;
    const MARGIN   = 48;
    const BOUNCE_F = 20;

    gameRef.current = {
      phase:       'countdown', // 'countdown' | 'playing' | 'over'
      countdown:   3,
      cdTimer:     0,
      goFrames:    0,           // 0→30 "GO!" display
      timeLeft:    30,
      timeTimer:   0,
      score:       0,
      streak:      0,
      bestStreak:  0,
      hitsTotal:   0,
      missesTotal: 0,
      hearts:      [],
      particles:   [],
      popups:      [],
      waveTimer:   0,
      nextWave:    50,
      pulse:       0,
      deathFrames: 0,
      animationId: null,
    };
    const game = gameRef.current;

    // ── Heart shape ──────────────────────────────────────────
    const heartPath = (cx, cy, s) => {
      ctx.beginPath();
      ctx.moveTo(cx,               cy + s * 0.40);
      ctx.bezierCurveTo(cx - s*0.10, cy + s*0.20, cx - s*0.65, cy - s*0.10, cx - s*0.65, cy - s*0.15);
      ctx.bezierCurveTo(cx - s*0.65, cy - s*0.55, cx - s*0.10, cy - s*0.65, cx,           cy - s*0.35);
      ctx.bezierCurveTo(cx + s*0.10, cy - s*0.65, cx + s*0.65, cy - s*0.55, cx + s*0.65, cy - s*0.15);
      ctx.bezierCurveTo(cx + s*0.65, cy - s*0.10, cx + s*0.10, cy + s*0.20, cx,           cy + s*0.40);
      ctx.closePath();
    };

    // ── Difficulty helpers ───────────────────────────────────
    const phase = () => {
      const e = 30 - game.timeLeft;
      return e < 10 ? 0 : e < 20 ? 1 : 2;
    };

    const getMultiplier = (streak) =>
      streak < 5 ? 1 : streak < 10 ? 2 : streak < 15 ? 3 : 4;

    // shrinkFrames per phase for each type
    const TYPE = {
      normal: { pts: 1, color: '#f43f5e', size: 28, sf: [120, 102, 84] },
      fast:   { pts: 2, color: '#c026d3', size: 24, sf: [72,  61,  50] },
      gold:   { pts: 5, color: '#FFD700', size: 34, sf: [90,  76,  63] },
    };

    // ── Spawning ─────────────────────────────────────────────
    const pickPos = () => {
      for (let i = 0; i < 12; i++) {
        const x = MARGIN + Math.random() * (canvas.width  - MARGIN * 2);
        const y = HUD_H + MARGIN + Math.random() * (canvas.height - HUD_H - MARGIN * 2);
        if (game.hearts.every(h => {
          const dx = h.x - x, dy = h.y - y;
          return dx*dx + dy*dy > 65*65;
        })) return { x, y };
      }
      return {
        x: MARGIN + Math.random() * (canvas.width  - MARGIN * 2),
        y: HUD_H + MARGIN + Math.random() * (canvas.height - HUD_H - MARGIN * 2),
      };
    };

    const spawnHeart = (type) => {
      const p = phase();
      const T = TYPE[type];
      const { x, y } = pickPos();
      game.hearts.push({
        id: Math.random(), x, y, type,
        baseSize: T.size, displaySize: 0, alpha: 1,
        bounceF: 0, lifeF: 0, shrinkF: T.sf[p],
        color: T.color, pts: T.pts, alive: true,
      });
    };

    const spawnWave = () => {
      const p = phase();
      // Count: 1 heart in phase 0, 1-2 in phase 1, 1-3 in phase 2
      const maxCounts = [1, 2, 3];
      const count = 1 + Math.floor(Math.random() * maxCounts[p]);
      // Type distribution
      const rolls = [
        ['normal'],
        ['normal','normal','normal','normal','fast'],
        ['normal','normal','normal','fast','fast','gold'],
      ][p];
      for (let i = 0; i < count; i++) {
        spawnHeart(rolls[Math.floor(Math.random() * rolls.length)]);
      }
    };

    const getWaveInterval = () => {
      const p = phase();
      const [min, max] = [[50,108],[40,88],[30,66]][p];
      // 20% chance of a calm pause (1.6× longer gap)
      return Math.random() < 0.2
        ? max * 1.6
        : min + Math.random() * (max - min);
    };

    // ── Particles ────────────────────────────────────────────
    const spawnParticles = (x, y, color, n) => {
      for (let i = 0; i < n; i++) {
        const a = (Math.PI * 2 * i) / n + (Math.random() - 0.5) * 0.8;
        const sp = 1.6 + Math.random() * 2.6;
        game.particles.push({
          x, y, vx: Math.cos(a)*sp, vy: Math.sin(a)*sp - 1.6,
          color, life: 1, size: 3 + Math.random() * 5,
        });
      }
    };

    // ── Update ───────────────────────────────────────────────
    const update = () => {
      // Countdown phase
      if (game.phase === 'countdown') {
        game.cdTimer++;
        if (game.cdTimer >= 60) {
          game.countdown--;
          game.cdTimer = 0;
          if (game.countdown <= 0) {
            game.countdown = 0;
            game.goFrames++;
          }
        } else if (game.countdown <= 0) {
          game.goFrames++;
          if (game.goFrames >= 32) game.phase = 'playing';
        }
        return;
      }

      if (game.phase !== 'playing') return;

      // Timer
      game.timeTimer++;
      if (game.timeTimer >= 60) {
        game.timeLeft--;
        game.timeTimer = 0;
        if (game.timeLeft <= 0) { game.timeLeft = 0; game.phase = 'over'; return; }
      }

      // Wave spawn
      game.waveTimer++;
      if (game.waveTimer >= game.nextWave) {
        spawnWave();
        game.nextWave = getWaveInterval();
        game.waveTimer = 0;
      }

      // Hearts
      for (const h of game.hearts) {
        if (!h.alive) continue;
        if (h.bounceF < BOUNCE_F) {
          h.bounceF++;
          const t = h.bounceF / BOUNCE_F;
          h.displaySize = h.baseSize * (t + Math.sin(t * Math.PI) * 0.22);
          h.alpha = 1;
        } else {
          h.lifeF++;
          h.alpha = Math.max(0, 1 - h.lifeF / h.shrinkF);
          h.displaySize = h.baseSize * h.alpha;
          if (h.lifeF >= h.shrinkF) {
            h.alive = false;
            game.streak = 0;
            game.missesTotal++;
            game.pulse = 20;
          }
        }
      }
      game.hearts = game.hearts.filter(h => h.alive);

      // Particles
      for (const p of game.particles) {
        p.x += p.vx; p.y += p.vy; p.vy += 0.07;
        p.life -= 0.036; p.size *= 0.97;
      }
      game.particles = game.particles.filter(p => p.life > 0);

      // Popups
      for (const p of game.popups) { p.y -= 1.4; p.life -= 0.028; }
      game.popups = game.popups.filter(p => p.life > 0);

      if (game.pulse > 0) game.pulse--;
    };

    // ── Render ───────────────────────────────────────────────
    const render = () => {
      const grad = ctx.createLinearGradient(0, 0, 0, canvas.height);
      grad.addColorStop(0, '#fce4f5');
      grad.addColorStop(1, '#fef0ff');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Miss pulse
      if (game.pulse > 0) {
        ctx.fillStyle = `rgba(180,0,100,${(game.pulse/20)*0.14})`;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      // Hearts
      for (const h of game.hearts) {
        if (!h.alive || h.displaySize <= 0) continue;
        ctx.save();
        ctx.globalAlpha = h.alpha;
        if (h.type === 'gold') {
          ctx.fillStyle = '#FFD700';
          ctx.shadowColor = 'rgba(255,215,0,0.7)';
          ctx.shadowBlur = 18;
        } else if (h.type === 'fast') {
          ctx.fillStyle = '#c026d3';
          ctx.shadowColor = 'rgba(192,38,211,0.5)';
          ctx.shadowBlur = 12;
        } else {
          ctx.fillStyle = '#f43f5e';
          ctx.shadowColor = 'rgba(244,63,94,0.35)';
          ctx.shadowBlur = 10;
        }
        heartPath(h.x, h.y, h.displaySize);
        ctx.fill();
        // Gold sparkle ring
        if (h.type === 'gold') {
          ctx.strokeStyle = 'rgba(255,255,160,0.7)';
          ctx.lineWidth = 1.5;
          ctx.shadowBlur = 0;
          ctx.beginPath();
          ctx.arc(h.x, h.y, h.displaySize * 0.9 + 4, 0, Math.PI * 2);
          ctx.stroke();
        }
        ctx.restore();
      }

      // Particles
      for (const p of game.particles) {
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.fillStyle = p.color;
        heartPath(p.x, p.y, p.size);
        ctx.fill();
        ctx.restore();
      }

      // Popups
      for (const p of game.popups) {
        ctx.save();
        ctx.globalAlpha = Math.min(1, p.life);
        ctx.fillStyle = p.color ?? '#111';
        ctx.font = `bold ${p.big ? 18 : 14}px Inter, system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.fillText(p.text, p.x, p.y);
        ctx.restore();
      }

      // ── HUD strip ────────────────────────────────────────
      ctx.fillStyle = 'rgba(255,255,255,0.72)';
      ctx.fillRect(0, 0, canvas.width, HUD_H);
      ctx.strokeStyle = 'rgba(240,180,220,0.45)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, HUD_H); ctx.lineTo(canvas.width, HUD_H);
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Score
      ctx.fillStyle = '#111';
      ctx.font = 'bold 22px Inter, system-ui, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(game.score, 16, 34);

      // Streak + multiplier (center)
      const mult = getMultiplier(game.streak);
      if (game.streak > 0) {
        ctx.textAlign = 'center';
        if (mult > 1) {
          ctx.fillStyle = '#c026d3';
          ctx.font = 'bold 14px Inter, system-ui, sans-serif';
          ctx.fillText(`🔥 ${game.streak}  ×${mult}`, canvas.width / 2, 22);
          ctx.font = '11px Inter, system-ui, sans-serif';
          ctx.fillStyle = '#999';
          ctx.fillText('streak', canvas.width / 2, 38);
        } else {
          ctx.fillStyle = '#f43f5e';
          ctx.font = 'bold 13px Inter, system-ui, sans-serif';
          ctx.fillText(`${game.streak} streak`, canvas.width / 2, 34);
        }
      }

      // Time (right)
      const tl = game.phase === 'countdown' ? 30 : game.timeLeft;
      ctx.fillStyle = tl <= 10 && game.phase === 'playing' ? '#e11d48' : '#111';
      ctx.font = `bold ${tl <= 10 && game.phase === 'playing' ? 22 : 20}px Inter, system-ui, sans-serif`;
      ctx.textAlign = 'right';
      ctx.fillText(`${tl}s`, canvas.width - 16, 34);

      // ── Countdown overlay ────────────────────────────────
      if (game.phase === 'countdown') {
        ctx.fillStyle = 'rgba(252, 228, 245, 0.88)';
        ctx.fillRect(0, HUD_H, canvas.width, canvas.height - HUD_H);
        ctx.textAlign = 'center';
        if (game.countdown > 0) {
          const pulse = 1 + Math.sin((game.cdTimer / 60) * Math.PI) * 0.18;
          ctx.save();
          ctx.translate(canvas.width / 2, canvas.height / 2 + 10);
          ctx.scale(pulse, pulse);
          ctx.fillStyle = '#f43f5e';
          ctx.font = 'bold 96px Inter, system-ui, sans-serif';
          ctx.fillText(game.countdown, 0, 0);
          ctx.restore();
          ctx.fillStyle = '#888';
          ctx.font = '16px Inter, system-ui, sans-serif';
          ctx.fillText('Get ready!', canvas.width / 2, canvas.height / 2 + 68);
        } else {
          // GO!
          const goT = game.goFrames / 32;
          const goScale = 1 + Math.sin(goT * Math.PI) * 0.12;
          ctx.save();
          ctx.translate(canvas.width / 2, canvas.height / 2 + 10);
          ctx.scale(goScale, goScale);
          ctx.fillStyle = '#c026d3';
          ctx.font = 'bold 72px Inter, system-ui, sans-serif';
          ctx.fillText('GO!', 0, 0);
          ctx.restore();
        }
      }

      // ── Tip screen before first wave ─────────────────────
      if (game.phase === 'playing' && game.hearts.length === 0 && game.hitsTotal === 0 && game.timeLeft === 30) {
        ctx.fillStyle = 'rgba(0,0,0,0)'; // invisible, hearts about to spawn
      }
    };

    // ── Game loop ────────────────────────────────────────────
    const gameLoop = () => {
      update();
      render();
      if (game.phase !== 'over') {
        game.animationId = requestAnimationFrame(gameLoop);
      } else {
        game.deathFrames++;
        if (game.deathFrames < 25) {
          game.animationId = requestAnimationFrame(gameLoop);
        } else {
          setScore(game.score);
          setGameOver(true);
        }
      }
    };

    // ── Controls ─────────────────────────────────────────────
    const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    const handlePointerDown = (e) => {
      if (game.phase !== 'playing') return;
      e.preventDefault();

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width  / rect.width;
      const scaleY = canvas.height / rect.height;
      const src = e.touches ? e.touches[0] : e;
      const cx = (src.clientX - rect.left) * scaleX;
      const cy = (src.clientY - rect.top)  * scaleY;

      for (let i = game.hearts.length - 1; i >= 0; i--) {
        const h = game.hearts[i];
        if (!h.alive || h.displaySize <= 0) continue;
        const dx = cx - h.x, dy = cy - h.y;
        const hitR = Math.max(isMobile ? 26 : 20, h.displaySize * (isMobile ? 1.05 : 0.82));
        if (dx*dx + dy*dy <= hitR*hitR) {
          h.alive = false;
          const mult = getMultiplier(game.streak);
          const pts  = h.pts * mult;
          game.score += pts;
          game.streak++;
          game.hitsTotal++;
          if (game.streak > game.bestStreak) game.bestStreak = game.streak;
          spawnParticles(h.x, h.y, h.type === 'gold' ? '#FFD700' : h.color, 10);
          const label = mult > 1 ? `×${mult} +${pts}` : `+${pts}`;
          game.popups.push({
            x: cx, y: cy - 10, text: label,
            color: h.type === 'gold' ? '#b45309' : mult > 1 ? '#c026d3' : '#111',
            life: 1.3, big: mult > 1,
          });
          break;
        }
      }
    };

    gameLoop();
    canvas.addEventListener('mousedown', handlePointerDown);
    canvas.addEventListener('touchstart', handlePointerDown, { passive: false });

    return () => {
      cancelAnimationFrame(game.animationId);
      canvas.removeEventListener('mousedown', handlePointerDown);
      canvas.removeEventListener('touchstart', handlePointerDown);
    };
  }, [restartTrigger]);

  const handlePlayAgain = () => {
    setScore(0);
    setGameOver(false);
    setGameStats(null);
    setTopScores([]);
    setRestartTrigger(t => t + 1);
  };

  return (
    <div className="game-container">
      <div className="game-header">
        <div className="settings-menu" ref={settingsRef}>
          <button className="settings-btn" onClick={() => setSettingsOpen(v => !v)} aria-label="Settings">⚙️</button>
          {settingsOpen && (
            <div className="settings-dropdown">
              <div className="settings-email">{user.email}</div>
              <hr className="settings-divider" />
              <button className="settings-item" onClick={() => { setSettingsOpen(false); onNavigate('edit-profile'); }}>Edit Username</button>
              {profile?.is_admin && (
                <button className="settings-item" onClick={() => { setSettingsOpen(false); onNavigate('admin'); }}>Admin Panel</button>
              )}
              <hr className="settings-divider" />
              <button className="settings-item settings-item-danger" onClick={() => supabase.auth.signOut()}>Logout</button>
            </div>
          )}
        </div>
        <span className="game-username">{profile?.username || user.email}</span>
        <button className="btn back-btn" onClick={() => navigate('/')}>← Games</button>
      </div>

      <h1 className="game-title">🫧 Flutter Catch (Beta)</h1>
      <canvas ref={canvasRef} width={500} height={600} className="game-canvas" style={{ cursor: 'crosshair' }} />

      <button className="btn leaderboard-btn" onClick={() => onNavigate('leaderboard')}>
        🏆 Leaderboard
      </button>
      <button className="btn leaderboard-btn" onClick={() => onNavigate('leagues')}>
        🏅 Leagues
      </button>

      {gameOver && gameStats && (
        <div className="game-over-modal">
          <div className="game-over-box flutter-catch-over">
            <h2>Time's up! 🩷</h2>
            <p className="final-score">{gameStats.score}</p>

            <div className="catch-stats">
              <div className="catch-stat">
                <span className="catch-stat-val">{gameStats.hits}</span>
                <span className="catch-stat-label">Caught</span>
              </div>
              <div className="catch-stat">
                <span className="catch-stat-val">{gameStats.misses}</span>
                <span className="catch-stat-label">Missed</span>
              </div>
              <div className="catch-stat">
                <span className="catch-stat-val">{gameStats.bestStreak}</span>
                <span className="catch-stat-label">Best streak</span>
              </div>
            </div>

            <div className="catch-leaderboard">
              <div className="catch-lb-title">Top Scores</div>
              {topScores.length === 0 ? (
                <p className="catch-lb-loading">{scoreSaved ? 'No scores yet' : 'Loading…'}</p>
              ) : (
                <table className="leaderboard-table">
                  <thead>
                    <tr><th>Rank</th><th>Player</th><th>Score</th></tr>
                  </thead>
                  <tbody>
                    {topScores.map((entry, i) => (
                      <tr key={entry.id} className={entry.user_id === user.id ? 'user-row' : ''}>
                        <td>#{i + 1}</td>
                        <td>{entry.username || entry.email}</td>
                        <td>{entry.score}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="catch-actions">
              <button onClick={handlePlayAgain} className="btn">Play Again</button>
              <button onClick={() => onNavigate('leaderboard')} className="btn btn-submit" disabled={!scoreSaved}>
                {scoreSaved ? 'Full Leaderboard' : 'Saving…'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
